import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, ButtonBase } from '@material-ui/core';
import { CheckboxesComponent, Inputs, Spinner } from '../../../Components';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Check from '@material-ui/icons/Check';
import StepConnector from '@material-ui/core/StepConnector';
import './ArchiveWizardDialogView.scss';
import { returnPropsByPermissions } from '../../../Helper' ; 
import { ArchiveUnit, ArchiveDeveloper, LeadArchival, leadDetailsGet, unitDetailsGet, GetDeveloperContactByIdAPI, contactsDetailsGet, GetContacts } from '../../../Services';
import { UnitsStatusEnum } from '../../../Enums';
import { 
  UnitsSalesPermissions ,
  UnitsLeasePermissions ,
  LeadOwnerUnitSalePermissions , 
  LeadOwnerUnitLeasePermissions  , 
  PrimaryUnitsPermissions , 
  ResaleUnitsPermissions , 
  LeadsSalesPermissions , 
  LeadsLeasePermissions  ,
  LeadsCAllCenterPermissions , 
  LeadOwnerLeadsPermissions , 
  LeadsMortgagesPermissions , 
} from '../../../Permissions' ;  

// Custom Connector (QontoConnector)
const QontoConnector = withStyles({
  alternativeLabel: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  active: {
    '& $line': {
      borderColor: '#2153b1',
    },
  },
  completed: {
    '& $line': {
      borderColor: '#2153b1',
    },
  },
  line: {
    borderColor: '#eaeaf0',
    borderTopWidth: 3,
    borderRadius: 1,
  },
})(StepConnector);

// Custom Step Icon (QontoStepIcon)
const useQontoStepIconStyles = makeStyles({
  root: {
    color: '#eaeaf0',
    display: 'flex',
    height: 22,
    alignItems: 'center',
  },
  active: {
    color: '#2153b1',
  },
  circle: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: 'currentColor',
  },
  completed: {
    color: '#2153b1',
    zIndex: 1,
    fontSize: 18,
  },
});

function QontoStepIcon(props) {
  const classes = useQontoStepIconStyles();
  const { active, completed } = props;

  return (
    <div
      className={clsx(classes.root, {
        [classes.active]: active,
      })}
    >
      {completed ? <Check className={classes.completed} /> : <div className={classes.circle} />}
    </div>
  );
}

QontoStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
};

export const ArchiveWizard = ({
  isOpen,
  onClose,
  parentTranslationPath,
  reloadData,
  ArchiveWizardType = 'Unit',
  selectedLeads,
  state,
  unitType = 'Sale', 
  type  
}) => {
  const { t } = useTranslation(['Shared', 'ArchiveWizard']);
  const translationPath = '';

  const [isLoading, setIsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [validationState, setValidationState] = useState({});
  const [developerId, setDeveloperId] = useState('');
  const [contactId, setContactId] = useState('');
  const [useDeveloperId, setUseDeveloperId] = useState(true);
  const [developerName, setDeveloperName] = useState('');
  const [leadId, setLeadId] = useState('');
  const [unitId, setUnitId] = useState(state?.unitParams?.id || '');
  const [validationMessage, setValidationMessage] = useState('');
  const [search, setSearch] = useState({ lead: '' });
  const [localState, setLocalState] = useState({ lead: null });
  const [unitData, setUnitData] = useState(null);
  const [leadNotFound, setLeadNotFound] = useState(false);
  const [unitNotFound, setUnitNotFound] = useState(false);
  const [developerNotFound, setDeveloperNotFound] = useState(false);
  const [activeContactData, setActiveContactData] = useState(null);
  const [developerData, setDeveloperData] = useState(null);
  const [archiveResult, setArchiveResult] = useState(null);

  const steps = {
    Unit: [
      t(`${translationPath}findUnit`),
      t(`${translationPath}validate`),
      t(`${translationPath}confirm`),
    ],
    Lead: [
      t(`${translationPath}identifyLead`),
      t(`${translationPath}validate`),
      t(`${translationPath}confirm`),
    ],
    Developer: [
      t(`${translationPath}identifyDeveloper`),
      t(`${translationPath}validate`),
      t(`${translationPath}confirm`),
    ],
  }[ArchiveWizardType];

  const handleSearch = async () => {
    if (!leadId || leadId === '') return;
    setIsLoading(true);
    setLeadNotFound(false);
    try {
      const res = await leadDetailsGet({ id: leadId });
      if (!(res && res.status && res.status !== 200)) {
        setLocalState({ lead: res });
        setSearch({ lead: leadId });
      } else {
        setLocalState({ lead: null });
        setSearch({ lead: '' });
        setLeadNotFound(true);
      }
    } catch (error) {
      setLocalState({ lead: null });
      setSearch({ lead: '' });
      setLeadNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getUnitById = useCallback(async () => {
    if (!unitId || unitId === '') return;
    setIsLoading(true);
    setUnitNotFound(false);
    try {
      const res = await unitDetailsGet({ id: unitId });
      if (res && res.status && res.status !== 200) {
        if (res.status === 500) {
          setUnitNotFound(true);
        }
        setUnitData(null);
      } else {
        setUnitData(res);
        setUnitNotFound(false);
      }
    } catch (error) {
      setUnitData(null);
    } finally {
      setIsLoading(false);
    }
  }, [unitId]);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const getDeveloperContactById = useCallback(async (id) => {
    if (!id || id === '') return;
    setIsLoading(true);
    setDeveloperNotFound(false);
    try {
      const res = await GetDeveloperContactByIdAPI(id);
      if (res && (!('status' in res) || (res.status && res.status === 200))) {
        if (res.developerContactJson && res.developerContactJson.developer) {
          const developerContactJson = res.developerContactJson.developer;
          const activeDeveloper = {
            developer: {
              id: res.developerContactId || id,
              name: developerContactJson.developer_name?.name || '',
              company_name: developerContactJson.developer_name?.company_name || null,
              mobile: developerContactJson.developer_name?.mobile || null,
              landline_number: developerContactJson.landline_number?.phone || null,
              email_address: developerContactJson.email_address || null,
              city: developerContactJson.city?.lookupItemName || null,
              country: developerContactJson.country?.lookupItemName || null,
              contact_type_id: developerContactJson.developer_name?.type || null,
              language: developerContactJson.language?.lookupItemName || null,
            },
          };
          setActiveContactData(activeDeveloper);
          setDeveloperData(activeDeveloper.developer);
          setDeveloperName(activeDeveloper.developer.name);
        } else {
          setDeveloperData(null);
          setActiveContactData(null);
          setDeveloperNotFound(true);
        }
      } else {
        setDeveloperData(null);
        setActiveContactData(null);
        setDeveloperNotFound(true);
      }
    } catch (error) {
      setDeveloperData(null);
      setActiveContactData(null);
      setDeveloperNotFound(true);
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, []);

  const contactById = useCallback(async (id) => {
    setIsLoading(true);
    try {
      const res = await GetContacts({
        pageSize: 1,
        pageIndex: 0,
        search: id,
      });

      if (res && res.result && res.result.length > 0) {
        const contact = res.result[0];
        const contactData = {
          developer: {
            id: contact.contactsId,
            name: contact.contact.company_name || contact.contact.name || '',
            company_name: contact.contact.company_name || null,
            mobile: null,
            email_address: contact.contact.general_email?.email || null,
            landline_number: contact.contact.landline_number?.phone || null,
            city: contact.contact.city?.lookupItemName || null,
            country: contact.contact.country?.lookupItemName || null,
            contact_type_id: contact.contact.contact_type_id,
            language: contact.contact.language?.lookupItemName || null,
          },
        };
        setActiveContactData(contactData);
        setDeveloperData(contactData.developer);
        setDeveloperName(contactData.developer.name);
        setDeveloperNotFound(false);
      } else {
        setDeveloperData(null);
        setActiveContactData(null);
        setDeveloperNotFound(true);
      }
    } catch (error) {
      setDeveloperData(null);
      setActiveContactData(null);
      setDeveloperNotFound(true);
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, []);

  const handleNext = async () => {
    if (activeStep === 0 && !validateFindStep()) return;
    if (activeStep === 1 && !validateValidationStep()) return;
    if (activeStep === 2) {
      setIsLoading(true);
      let result;
      try {
        if (ArchiveWizardType === 'Lead') {
          result = await LeadArchival({
            leadId: parseInt(leadId),
            skipActivityValidation: validationState.skipActivityValidation || false,
            skipDraftUnitOwnershipValidation: validationState.skipDraftUnitOwnershipValidation || false,
          });
        } else if (ArchiveWizardType === 'Developer') {
          result = await ArchiveDeveloper(useDeveloperId ? developerId : contactId, !useDeveloperId ? contactId : null);
        } else if (ArchiveWizardType === 'Unit') {
          result = await ArchiveUnit(
            { skipActivitiesCheck: validationState.skipActivitiesCheck || false, unitId: unitId },
            {}
          );
        }
        if (result && result.success) {
          if (ArchiveWizardType === 'Developer') {
            setArchiveResult({
              message: `Developer Archived!\nThe developer ${developerName || t(`${translationPath}developerDefaultName`, { id: developerId || contactId })} has been archived.\nAll linked references have been updated.`,
              icon: 'mdi-checkbox-marked-circle-outline mr-2'
            });
          } else {
            setArchiveResult({ message: 'Unit archived successfully. All linked references removed', icon: 'mdi-checkbox-marked-circle-outline mr-2' });
          }
          if (reloadData) reloadData();
        } else if (result && !result.success) {
          setArchiveResult({ message: result?.message || result?.errors?.[0] || t(`${translationPath}unknownError`), icon: 'mdi mdi-alert-circle-outline mr-2 ' });
        } else {
          setArchiveResult({ message: t(`${translationPath}archiveFailedGeneric`), icon: 'mdi mdi-alert-circle-outline mr-2 ' });
        }
      } catch (error) {
        setArchiveResult({ message: error?.response?.message || t(`${translationPath}unknownError`), icon: 'mdi mdi-alert-circle-outline mr-2 ' });
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
          setActiveStep(3);
        }
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 3) {
      setArchiveResult(null);
      setActiveStep(2);
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };
  const handleReset = () => setActiveStep(0);

  const validateFindStep = () => {
    if (ArchiveWizardType === 'Unit') return unitId && unitId !== '123' && unitData && !unitNotFound;
    if (ArchiveWizardType === 'Lead') return leadId && leadId !== '234' && localState.lead && !leadNotFound;
    if (ArchiveWizardType === 'Developer') {
      const id = useDeveloperId ? developerId : contactId;
      const isValidContactType = useDeveloperId || (developerData && [2].includes(developerData.contact_type_id));
      return id && id !== 'invalid' && !id.includes('archived') && developerData && Object.keys(developerData).length > 0 && !developerNotFound && isValidContactType;
    }
    return false;
  };

  const validateValidationStep = () => {
    if (ArchiveWizardType === 'Unit') {
      return true;
    }
    if (ArchiveWizardType === 'Lead') {
      if (validationMessage) {
        return validationState.skipActivityValidation || validationState.skipDraftUnitOwnershipValidation;
      }
      return true;
    }
    if (ArchiveWizardType === 'Developer') {
      if (validationMessage) {
        return true;
      }
      return Object.values(validationState).every((val) => val === true);
    }
    return false;
  };

  const handleCheckboxChange = useCallback((item, index, checked) => {
    setValidationState((prev) => ({ ...prev, [item.label]: checked }));
  }, []);const isDisabledCheckboxesPermissions = (key, checkBoxName) => {
  
  if (!type) {
    console.warn(`Type is undefined or null. Disabling checkbox by default.`);
    return true; // Disable if type is undefined or null
  }

  if (key === 'unit' && checkBoxName === 'skipActivitiesCheck') {
    switch (type) {
      case 'UnitsSales':
        return !returnPropsByPermissions(UnitsSalesPermissions.CanSkipActivitiesCheck.permissionsId);
      case 'UnitsLease':
        return !returnPropsByPermissions(UnitsLeasePermissions.CanSkipActivitiesCheck.permissionsId);
      case 'PrimaryUnits':
        return !returnPropsByPermissions(PrimaryUnitsPermissions.CanSkipActivitiesCheck.permissionsId);
      case 'UnitsSaleLeadOwner':
        return !returnPropsByPermissions(LeadOwnerUnitSalePermissions.CanSkipActivitiesCheck.permissionsId);
      case 'UnitsLeaseLeadOwner':
        return !returnPropsByPermissions(LeadOwnerUnitLeasePermissions.CanSkipActivitiesCheck.permissionsId);
      case 'UnitsResale':
        return !returnPropsByPermissions(ResaleUnitsPermissions.CanSkipActivitiesCheck.permissionsId);
      default:
        console.warn(`Unknown type for unit: ${type}`);
        return true; // Disable by default if type is unknown
    }
  } else if (key === 'lead') {
    if (checkBoxName === 'skipActivityValidation') {
      switch (type) {
        case 'leadsLeadOwner':
          return !returnPropsByPermissions(LeadOwnerLeadsPermissions.CanSkipLeadActivityValidation.permissionsId);
        case 'leadsLease':
          return !returnPropsByPermissions(LeadsLeasePermissions.CanSkipLeadActivityValidation.permissionsId);
        case 'leadsCallCenter':
          return !returnPropsByPermissions(LeadsCAllCenterPermissions.CanSkipLeadActivityValidation.permissionsId);
        case 'leadsMortgage':
          return !returnPropsByPermissions(LeadsMortgagesPermissions.CanSkipLeadActivityValidation.permissionsId);
        case 'leadsSales':
          return !returnPropsByPermissions(LeadsSalesPermissions.CanSkipLeadActivityValidation.permissionsId);
        default:
          console.warn(`Unknown type for lead (skipActivityValidation): ${type}`);
          return true; // Disable by default if type is unknown
      }
    } else if (checkBoxName === 'skipDraftUnitOwnershipValidation') {
      switch (type) {
        case 'leadsLeadOwner':
          return !returnPropsByPermissions(LeadOwnerLeadsPermissions.CanSkipLeadDraftUnitValidation.permissionsId);
        case 'leadsLease':
          return !returnPropsByPermissions(LeadsLeasePermissions.CanSkipLeadDraftUnitValidation.permissionsId);
        case 'leadsCallCenter':
          return !returnPropsByPermissions(LeadsCAllCenterPermissions.CanSkipLeadDraftUnitValidation.permissionsId);
        case 'leadsMortgage':
          return !returnPropsByPermissions(LeadsMortgagesPermissions.CanSkipLeadDraftUnitValidation.permissionsId);
        case 'leadsSales':
          return !returnPropsByPermissions(LeadsSalesPermissions.CanSkipLeadDraftUnitValidation.permissionsId);
        default:
          console.warn(`Unknown type for lead (skipDraftUnitOwnershipValidation): ${type}`);
          return true; // Disable by default if type is unknown
      }
    } else {
      console.warn(`Invalid checkBoxName for lead: ${checkBoxName}`);
      return true; // Disable if checkBoxName is invalid
    }
  }
  console.warn(`Invalid key or checkBoxName: key=${key}, checkBoxName=${checkBoxName}`);
  return true; // Disable by default if key or checkBoxName is invalid
};

  const getValidationContent = () => {
    if (ArchiveWizardType === 'Unit') {
      const unitCheckboxes = [
        { label: 'skipActivitiesCheck', text: t(`${translationPath}skipActivitiesCheck`) },
      ];
      return (
        <div className="validation-content">
          <div className="confirmation-content">
            <span className='mdi mdi-archive ' />
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {t(`${translationPath}archivingRequirements`)}
            </span>
            <br />
            <p>{t(`${translationPath}archiveUnitConditionsNote`)}</p>
            <ul>
              <li><span class="status-icon mdi mdi-checkbox-marked-circle-outline " /> {t(`${translationPath}notLinkedToTransactions`)}</li>
              <li><span class="status-icon mdi mdi-checkbox-marked-circle-outline " /> {t(`${translationPath}unitStatusIsDraft`)}</li>
              <li><span class="status-icon mdi mdi-checkbox-marked-circle-outline " /> {t(`${translationPath}noRecentListingActivity`)}</li>
            </ul>
            <p>{t(`${translationPath}allRequirementsMustBeMet`)}</p>
          </div>

          <CheckboxesComponent
            data={unitCheckboxes}
            idRef="unit-validation"
            translationPath={translationPath}
            parentTranslationPath="ArchiveWizard"
            labelInput="text"
            onSelectedCheckboxChanged={handleCheckboxChange}
            checked={(item) => !isDisabledCheckboxesPermissions('unit', item.label) && validationState[item.label] || false}
            wrapperClasses="validation-checkboxes"
            isDisabled={(item) => isDisabledCheckboxesPermissions('unit', item.label)}

          />
        </div>
      );
    }
    if (ArchiveWizardType === 'Lead') {
      const leadCheckboxes = [
        { label: 'skipActivityValidation', text: t(`${translationPath}skipActivityValidation`) },
        { label: 'skipDraftUnitOwnershipValidation', text: t(`${translationPath}skipDraftUnitOwnershipValidation`) },
      ];
      return (
        <div className="validation-content">
          <div className="static-text">
            <span className="mdi mdi-alert-circle-outline error" />
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {t(`${translationPath}ValidationChecklist`)}
            </span>
            <br />
            Contact Opportunities
            <p className="title">Archiving {t(`${translationPath}will-remove-this-lead-from`)}</p>
            <ul>
              <li>Not linked to any transactions (Approved, Pending, or Closed)</li>
              <li>No recent listing/leasing activity (last 90 days)</li>
              <li>All requirements must be met to archive this unit</li>
            </ul>
          </div>
          {validationMessage && <div className="error">{validationMessage}</div>}
<CheckboxesComponent
  data={leadCheckboxes}
  idRef="lead-validation"
  translationPath={translationPath}
  parentTranslationPath="ArchiveWizard"
  labelInput="text"
  onSelectedCheckboxChanged={handleCheckboxChange}
  checked={(item) => !isDisabledCheckboxesPermissions('lead', item.label) && validationState[item.label] || false}
  wrapperClasses="validation-checkboxes"
  isDisabled={(item) => isDisabledCheckboxesPermissions('lead', item.label)}
/>
        </div>
      );
    }
    if (ArchiveWizardType === 'Developer') {
      const developerCheckboxes = [
        { label: 'notLinkedToTransactedContact', text: t(`${translationPath}notLinkedToTransactedContact`) },
        { label: 'noActiveAgreement', text: t(`${translationPath}noActiveAgreement`) },
        { label: 'noUploadedAgreement', text: t(`${translationPath}noUploadedAgreement`) },
        { label: 'notPropertyOwner', text: t(`${translationPath}notPropertyOwner`) },
        { label: 'notMasterDeveloper', text: t(`${translationPath}notMasterDeveloper`) },
        { label: 'notSecondaryDeveloper', text: t(`${translationPath}notSecondaryDeveloper`) },
        { label: 'noLinkedActiveCourses', text: t(`${translationPath}noLinkedActiveCourses`) },
        { label: 'noActiveMarketingCampaigns', text: t(`${translationPath}noActiveMarketingCampaigns`) },
        { label: 'noAvailableUnits', text: t(`${translationPath}noAvailableUnits`) },
      ];
      return (
        <div className="validation-content">
          {validationMessage && <div className="error">{validationMessage}</div>}
          <div className="validation-checkboxes">
            <div className='mb-3 '><span className='mdi mdi-alert-circle-outline error mr-2'></span>Validation Checklist</div>
            {developerCheckboxes.map((item) => (
              <div key={item.label} className={`validation-item${validationState[item.label] ? ' checked' : ' unchecked'}`}>
                <span className={`status-icon ${validationState[item.label] ? 'mdi mdi-checkbox-marked-circle-outline success' : 'mdi mdi-checkbox-marked-circle-outline success'}`} />
                <span>{item.text}</span>
                {!validationState[item.label] && (
                  <span className="failed-hint">
                    {
                      {
                        '': t(`${translationPath}notLinkedToTransactedContact`),
                        '': t(`${translationPath}noActiveAgreement`),
                        '': t(`${translationPath}developerHasUploadedAgreement`),
                        '': t(`${translationPath}developerIsPropertyOwner`),
                        '': t(`${translationPath}developerIsMasterDeveloper`),
                        '': t(`${translationPath}developerIsSecondaryDeveloper`),
                        '': t(`${translationPath}developerHasActiveCourses`),
                        '': t(`${translationPath}developerHasActiveCampaigns`),
                        '': t(`${translationPath}developerOwnsAvailableUnits`),
                      }[item.label]
                    }
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const getConfirmationContent = () => {
    if (ArchiveWizardType === 'Unit') {
      return (
        <div className="confirmation-content">
          <p className="title">{t(`${translationPath}archivingConfirmation`)}</p>
          <p>Archiving unit {unitId} will:</p>
          <ul>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}closeOwnerLeads`)}</li>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}removeUnitFromLeads`)}</li>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}setUnitStatusArchived`)}</li>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}recordActionInAuditLog`)}</li>
          </ul>
        </div>
      );
    }
    if (ArchiveWizardType === 'Lead') {
      const leadName = localState?.lead?.lead?.contact_name?.name || t(`${translationPath}unknown`);
      return (
        <div className="confirmation-content">
          <p className="title">{t(`${translationPath}archivingConfirmation`)}</p>
          <p>Archiving lead {leadId} ({leadName}) will remove this lead from:</p>
          <ul>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}contactOpportunities`)}</li>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}leadsPool`)}</li>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}activities`)}</li>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}callAnalyses`)}</li>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}rotation`)}</li>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}favorites`)}</li>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}developerModule`)}</li>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}notifications`)}</li>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}matchingUnits`)}</li>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}unqualifiedLeads`)}</li>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}inquirySource`)}</li>
            <li><span className='mdi mdi-minus-circle-outline' /> {t(`${translationPath}amlRecords`)}</li>
          </ul>
        </div>
      );
    }
    if (ArchiveWizardType === 'Developer') {
      return (
        <div className="confirmation-content">
          <div>
            <span className='mdi mdi-archive ' />
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
              {t(`${translationPath}archivingConfirmation`)}
            </span>
            <br />
            <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
              <p>{t(`${translationPath}developerArchivingMessage`)}{'  '}{developerName}{'  '}{t(`${translationPath}will`)} </p>
            </span>
            <ul>
              <li><span className='mdi mdi-minus-circle-outline' />{t(`${translationPath}removeDeveloperReferences`)}</li>
              <li><span className='mdi mdi-minus-circle-outline' />{t(`${translationPath}deactivateDeveloper`)}</li>
              <li><span className='mdi mdi-minus-circle-outline' />{t(`${translationPath}setDeveloperStatusArchived`)}</li>
            </ul>
          </div>
        </div>
      );
    }
    return null;
  };

  const getStatusContent = () => {
    return archiveResult && (
      <div className="confirmation-content">
        <p className="title">{t(`${translationPath}status`)}</p>
        <p>
          <span className={archiveResult.icon} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {archiveResult.message}
        </p>
      </div>
    );
  };

  const getStepContent = (step) => {
    if (step === 3) {
      return getStatusContent();
    }
    switch (step) {
      case 0:
        if (ArchiveWizardType === 'Unit') {
          return (
            <div className="step-content">
              <div className="d-flex aligncenter">
                <Inputs
                  idRef="unit-id-input"
                  value={unitId}
                  onInputChanged={(e) => setUnitId(e.target.value)}
                  labelValue={t(`${translationPath}unitIdLabel`)}
                  translationPath={translationPath}
                  parentTranslationPath="ArchiveWizard"
                  placeholder={t(`${translationPath}enterUnitId`)}
                  isRequired
                  wrapperClasses="mb-4 flex-grow-1"
                />
                <div className="flex">
                  <ButtonBase
                    className="ml-2 btns theme-solid clear-all-btn"
                    type="button"
                    onClick={getUnitById}
                    disabled={!unitId || isLoading}
                  >
                    {t(`${translationPath}Find`)}
                  </ButtonBase>
                </div>
              </div>
              {unitNotFound && <div className="error">{t(`${translationPath}unitNotFound`)}</div>}
              {unitData && (
                <div className="unit-card">
                  <div className="unit-title">
                    {unitData.unit.property_name?.name || 'Unknown Property'}, {unitData.unit.property_name?.community_name || ''}
                  </div>
                  <div>
                    <span className="unit-label">Unit Type: {unitData.unit.unit_type?.lookupItemName || ''}</span>
                    <span className="unit-label">ID: {unitId}</span>
                    <span className="unit-label">Unit Number: {unitData?.unit?.unit_number || ''}</span>
                    <div className="unit-label">Unit Ref: {unitData?.unit?.unit_ref_no}</div>
                    {
                      unitData && unitType === 'Sale' && (
                        <span className="unit-label">
                          {
                            unitData.saleStatus === 6 && 'Sold' || Object.values(UnitsStatusEnum).find(status => status.key === unitData.saleStatus)?.display ||
                            ''
                          }
                        </span>
                      )
                    }
                    {
                      unitData && unitType === 'Lease' && (
                        <span className="unit-label">
                          {
                            Object.values(UnitsStatusEnum).find(status => status.key === unitData.leaseStatus)?.display ||
                            unitData?.unit?.lease_status || ''
                          }
                        </span>
                      )
                    }
                  </div>
                </div>
              )}
            </div>
          );
        }
        if (ArchiveWizardType === 'Lead') {
          return (
            <div className="step-content">
              <div className="d-flex aligncenter">
                <Inputs
                  idRef="lead-id-input"
                  value={leadId}
                  onInputChanged={(e) => setLeadId(e.target.value)}
                  labelValue={t(`${translationPath}leadIdLabel`)}
                  translationPath={translationPath}
                  parentTranslationPath="ArchiveWizard"
                  placeholder={t(`${translationPath}enterLeadId`)}
                  isRequired
                  wrapperClasses="mb-4 flex-grow-1"
                />
                <div className="flex">
                  <ButtonBase
                    className="ml-2 btns theme-solid clear-all-btn"
                    type="button"
                    onClick={handleSearch}
                    disabled={!leadId || isLoading}
                  >
                    {t(`${translationPath}Find`)}
                  </ButtonBase>
                </div>
              </div>
              {leadNotFound && <div className="error">Lead not found</div>}
              {localState.lead && leadId !== 'archived' && (
                <div className="unit-card">
                  <div className="unit-title">
                    {localState.lead.lead.contact_name?.name || t(`${translationPath}unknown`)},
                  </div>
                  <div>
                    <span className="unit-label">ID: {leadId}</span>
                    <span className="unit-label">{t(`${translationPath}status`)}: {localState.lead.lead.status?.lookupItemName || t(`${translationPath}unknown`)}</span>
                    <span className="unit-label">{t(`${translationPath}stage`)}: {localState.lead.lead.lead_stage?.lookupItemName || t(`${translationPath}unknown`)}</span>
                  </div>
                </div>
              )}
              {leadId && leadId === 'archived' && <div className="error">{t(`${translationPath}leadAlreadyArchived`)}</div>}
            </div>
          );
        }
        if (ArchiveWizardType === 'Developer') {
          return (
            <div className="step-content">
              <div className="radio-group ">
                <div>
                  <span className='mdi mdi-archive ' />

                  <span style={{ fontSize: '16px', fontWeight: 'bold' }}>

                    {t(`${translationPath}archiveADeveloper`, 'Archive a Developer')}

                  </span>
                  <br />
                  <span style={{ fontSize: '12px', fontWeight: 'bold' }}>

                    {t(`${translationPath}enterDeveloperOrContactId`, 'Enter a Developer ID or Contact ID.')}
                  </span>
                </div>
                <label className='mt-3'>
                  <input
                    type="radio"
                    checked={useDeveloperId}
                    onChange={() => {
                      setUseDeveloperId(true);
                      setDeveloperData(null);
                      setActiveContactData(null);
                      setDeveloperName('');
                      setDeveloperNotFound(false);
                      setDeveloperId('');
                    }}
                  /> {t(`${translationPath}developerIdLabel`)}
                </label>
                <label>
                  <input
                    type="radio"
                    checked={!useDeveloperId}
                    onChange={() => {
                      setUseDeveloperId(false);
                      setDeveloperData(null);
                      setActiveContactData(null);
                      setDeveloperName('');
                      setDeveloperNotFound(false);
                      setContactId('');
                    }}
                  /> {t(`${translationPath}contactIdLabel`)}
                </label>
              </div>
              <div style={{ fontFamily: 'inherit', textTransform: 'capitalize' }}>
                <div className="d-flex aligncenter">
                  <Inputs
                    idRef="developer-id-input"
                    value={useDeveloperId ? developerId : contactId}
                    onInputChanged={(e) => (useDeveloperId ? setDeveloperId(e.target.value) : setContactId(e.target.value))}
                    labelValue={t(useDeveloperId ? `${translationPath}developerIdLabel` : `${translationPath}contactIdLabel`)}
                    translationPath={translationPath}
                    parentTranslationPath="ArchiveWizard"
                    placeholder={t(useDeveloperId ? `${translationPath}enterDeveloperId` : `${translationPath}enterContactId`)}
                    isRequired
                    wrapperClasses="mb-4 flex-grow-1"
                  />
                  <div className="flex">
                    <ButtonBase
                      className="ml-2 btns theme-solid clear-all-btn"
                      type="button"
                      onClick={() => (useDeveloperId ? getDeveloperContactById(developerId) : contactById(contactId))}
                      disabled={(!useDeveloperId && !contactId || useDeveloperId && !developerId || isLoading)}
                    >
                      {t(`${translationPath}Find`)}
                    </ButtonBase>
                  </div>
                </div>
                {developerNotFound && (
                  <div className="error">
                    {useDeveloperId ? t(`${translationPath}developerNotFound`) : t(`${translationPath}contactNotFound`)}
                  </div>
                )}
                {(useDeveloperId && developerId === 'invalid') && <div className="error">{t(`${translationPath}developerNotFound`)}</div>}
                {(!useDeveloperId && contactId === 'invalid') && <div className="error">{t(`${translationPath}contactNotDeveloper`)}</div>}
                {(developerId === 'archived' || contactId === 'archived') && <div className="error">{t(`${translationPath}developerAlreadyArchived`)}</div>}
                {/* {developerData && (useDeveloperId ? developerId : contactId) && !(developerId === 'invalid' || contactId === 'invalid' || developerId === 'archived' || contactId === 'archived') && (
                  <div className='box-Identify'>
                    <div>{t(`${translationPath}${useDeveloperId ? 'developerName' : 'contactName'}`)}: {developerName}</div>
                    <div>{t(`${translationPath}developerIdLabel`)}: {useDeveloperId ? developerId : contactId}</div>
                  </div>
                )} */}
                {developerData && (useDeveloperId ? developerId : contactId) &&
                  !(developerId === 'invalid' || contactId === 'invalid' || developerId === 'archived' || contactId === 'archived') && (
                    <div className="unit-card mt-2">
                      <div>
                        <span className="unit-label">
                          {t(`${translationPath}${useDeveloperId ? 'developerName' : 'contactName'}`)}: {developerName || t(`${translationPath}unknown`)}
                        </span>
                        <span className="unit-label">
                          {t(`${translationPath}developerIdLabel`)}: {useDeveloperId ? developerId : contactId || t(`${translationPath}unknown`)}
                        </span>
                      </div>
                    </div>
                  )}
                {(!useDeveloperId && developerData && ![2].includes(developerData.contact_type_id)) && <div className="error">{t(`${translationPath}contactNotDeveloper`)}</div>}
              </div>
            </div>
          );
        }
        return t(`${translationPath}unknownStep`);
      case 1:
        return <div className="step-content">{getValidationContent()}</div>;
      case 2:
        return <div className="step-content">{getConfirmationContent()}</div>;
      default:
        return null;
    }
  };

  const handleDialogClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      setActiveStep(0);
      setArchiveResult(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      maxWidth="sm"
      classes={{ paper: 'archive-wizard-dialog' }}
      onClose={handleDialogClose}
    >
      <DialogTitle>{activeStep < 3 && t(`${translationPath}${ArchiveWizardType}ArchiveWizard`)}</DialogTitle>
      <DialogContent>
        <Spinner isActive={isLoading} className="spinner" />
        {activeStep < 3 && (
          <Stepper alternativeLabel activeStep={activeStep} connector={<QontoConnector />}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel StepIconComponent={QontoStepIcon}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
        <div>{getStepContent(activeStep)}</div>
      </DialogContent>
      <DialogActions className="dialog-footer-wrapper mt-5">
        {activeStep < 3 ? (
          <>
            <button type="button" onClick={handleDialogClose} className="btn cancel-btn">
              {t('cancel')}
            </button>
            {activeStep !== 0 && (
              <button
                type="button"
                disabled={activeStep === 0}
                onClick={handleBack}
                className="btn back-btn"
              >
                {t('back')}
              </button>
            )}
            <button
              type="button"
              onClick={handleNext}
              className="btn next-btn"
              disabled={
                (activeStep === 0 && !validateFindStep()) ||
                (activeStep === 1 && !validateValidationStep())
              }
            >
              {activeStep === 2 ? t(`${translationPath}archive`) : t(`${translationPath}next`)}
            </button>
          </>
        ) : (
          <button type="button" onClick={handleDialogClose} className="btn cancel-btn">
            {t('close')}
          </button>
        )}
      </DialogActions>
    </Dialog>
  );
};

ArchiveWizard.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  parentTranslationPath: PropTypes.string,
  translationPath: PropTypes.string,
  reloadData: PropTypes.func,
  ArchiveWizardType: PropTypes.oneOf(['Unit', 'Lead', 'Developer']),
  selectedLeads: PropTypes.array,
  state: PropTypes.object,
};